import { AuthService, REFRESH_OK, REFRESH_TERMINAL } from "./authservice";
import { expireSession } from "../config/session401";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../config/constants";
import i18n from "../i18n/index.js";

// ¿Hay credenciales guardadas (aunque estén vencidas)? Sin ellas no hay sesión que
// expirar: un 401 es de una llamada pública y no debe disparar el flujo de cierre.
function hasStoredSession() {
  const at = localStorage.getItem(ACCESS_TOKEN);
  const rt = localStorage.getItem(REFRESH_TOKEN);
  return !!((at && at !== "null") || (rt && rt !== "null"));
}

// fetch con credenciales para los módulos que no pasan por una instancia axios
// (y por tanto no tienen el interceptor de 401): streams NDJSON/SSE, REST
// sueltos y servicios de terceros del mismo JWT.
//
//  1. Antes de enviar pide un access vigente (renueva si faltan <5 min).
//  2. Si aun así vuelve 401, renueva UNA vez y reintenta UNA vez.
//  3. Todo lo demás (incluido un segundo 401) se propaga tal cual: un 401
//     puntual de un endpoint no es motivo para tocar la sesión.
//  4. Si el servidor rechaza la credencial del refresh (terminal), se da la
//     sesión por terminada con el mismo flujo que el interceptor axios.

/** Normaliza Headers | [[k,v]] | objeto a objeto plano (sin depender de que `Headers` exista). */
function toPlainHeaders(headers) {
  if (!headers) return {};
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return { ...headers };
}

function withBearer(init, token) {
  const headers = toPlainHeaders(init.headers);
  // Se elimina cualquier Authorization previo (en cualquier capitalización) para no duplicarlo.
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === "authorization") delete headers[key];
  }
  if (token) headers.Authorization = `Bearer ${token}`;
  return { ...init, headers };
}

/** Cierra la sesión por veredicto terminal del servidor, con el mismo flujo (toast + logout + /login) que el interceptor axios. */
export function expireSessionFromTerminal(reason) {
  const error = Object.assign(new Error("Session expired"), { response: { status: 401 } });
  expireSession(
    error,
    reason === "blocked" ? i18n.t("Tu cuenta fue bloqueada. Contacta a tu administrador.") : undefined
  );
}

/**
 * @param {string|URL|Request} url
 * @param {RequestInit} [init] el body debe poder enviarse dos veces (string, FormData, Blob), no un stream.
 * @returns {Promise<Response>}
 */
export async function authFetch(url, init = {}) {
  const hadSession = hasStoredSession();
  const ensured = await AuthService.ensureSession();

  if (ensured.status === REFRESH_TERMINAL && hadSession) {
    // El canje previo ya dio el veredicto: no se repite en el 401.
    expireSessionFromTerminal(ensured.reason);
    return fetch(url, withBearer(init, AuthService.getAccessTokenApi()));
  }

  const sentToken = ensured.accessToken;
  const response = await fetch(url, withBearer(init, sentToken));
  if (response.status !== 401 || !hadSession) return response;

  const renewed = await AuthService.refreshAfterUnauthorized(sentToken);
  if (renewed.status === REFRESH_TERMINAL) {
    expireSessionFromTerminal(renewed.reason);
    return response;
  }
  if (renewed.status !== REFRESH_OK || !renewed.accessToken) return response;

  return fetch(url, withBearer(init, renewed.accessToken));
}

export default authFetch;
